import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { tagsService, type Tag } from "@/services/tags.service";
import { sortTags, type TagSortMode } from "@/lib/utils/tagSorting";

export type TagWithCount = Tag & { count: number };

export interface TagStatsFilters {
  routing_class?: string;
  category?: string;
  overall_message_intent?: string;
  platform?: string;
}

const normalizeFilters = (filters?: TagStatsFilters): TagStatsFilters | null => {
  if (!filters) return null;
  const entries = Object.entries(filters).filter(([, v]) => Boolean(v));
  return entries.length > 0 ? (Object.fromEntries(entries) as TagStatsFilters) : null;
};

// Tags rarely change once created — hold them longer than stats. Both keep
// the app's default gcTime so they are still in memory when the disk cache
// is written (cachePersistence); Home's tag rail paints from it on reopen.
const LIST_STALE_MS = 5 * 60_000;
const STATS_STALE_MS = 60_000;

// Query keys match StashTagManager so tag mutations (create/rename/delete) invalidate these entries.
export const listTagsQueryOptions = ({ includeSuggested = false } = {}) => ({
  queryKey: includeSuggested ? ["user-tags", { includeSuggested: true }] as const : ["user-tags"] as const,
  queryFn: () => tagsService.listTags(includeSuggested ? { includeSuggested: true } : undefined),
  staleTime: LIST_STALE_MS,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
});

export const tagStatsQueryOptions = (filters?: TagStatsFilters) => {
  const normalized = normalizeFilters(filters);
  return {
    queryKey: ["tag-stats", normalized] as const,
    queryFn: () => tagsService.getTagStats(normalized ?? undefined),
    staleTime: STATS_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };
};

interface QueryGate {
  enabled?: boolean;
}

export function useListTags({ enabled = true, includeSuggested = false }: QueryGate & { includeSuggested?: boolean } = {}) {
  return useQuery({
    ...listTagsQueryOptions({ includeSuggested }),
    enabled,
    select: tags => (Array.isArray(tags) ? tags : []),
  });
}

export function useTagStats(filters?: TagStatsFilters, { enabled = true }: QueryGate = {}) {
  return useQuery({ ...tagStatsQueryOptions(filters), enabled });
}

interface UseTagsWithCountsOptions extends QueryGate {
  filters?: TagStatsFilters;
  sort?: TagSortMode;
}

export function useTagsWithCounts({
  filters,
  sort = "recent",
  enabled = true,
}: UseTagsWithCountsOptions = {}) {
  const tagsQuery = useListTags({ enabled });
  const statsQuery = useTagStats(filters, { enabled });

  const data = useMemo<TagWithCount[]>(() => {
    if (!tagsQuery.data || !statsQuery.data) return [];
    const withCounts = tagsQuery.data
      .map(tag => ({ ...tag, count: statsQuery.data[tag.id] ?? 0 }))
      .filter(tag => tag.count > 0);
    return sortTags(withCounts, sort);
  }, [tagsQuery.data, statsQuery.data, sort]);

  return {
    data,
    isLoading: tagsQuery.isLoading || statsQuery.isLoading,
    isFetching: tagsQuery.isFetching || statsQuery.isFetching,
    isError: tagsQuery.isError || statsQuery.isError,
  };
}
